import { flatMap } from "lodash";
import { useMemo } from "react";
import {
  EngagementSurveyDeliveryFragment,
  GroupedAnswersNode,
} from "types/graphql-schema";

import AssessmentLayout from "@apps/assessments/components/layouts/assessment-layout";
import { AssessmentSection } from "@apps/assessments/components/questions/types";
import { EngagementSurveyReportViewOptions } from "@apps/reporting/components/engagement-survey-report/types";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";

import EngagementSurveyDeliveryQuestion from "./engagement-survey-delivery-question";

const EngagementSurveyDeliveryQuestionSections = ({
  assessmentDeliveryData,
  viewOptions,
}: {
  assessmentDeliveryData?: EngagementSurveyDeliveryFragment | null;
  viewOptions: EngagementSurveyReportViewOptions;
}) => {
  const sectionNodes = useMemo(() => {
    if (!assessmentDeliveryData) {
      return [];
    }
    const assessmentTemplate = assertNonNull(
      assessmentDeliveryData.complianceProgram?.engagementSurveyTemplate,
    );
    const questionSets = assertEdgesNonNull(assessmentTemplate.questionSets);
    return flatMap(questionSets, (set) => {
      const newSections: AssessmentSection[] = assertEdgesNonNull(
        set.sections,
      ).map((section) => {
        const questions = assertEdgesNonNull(section.questions);
        return {
          id: section.id,
          title: section.title,
          description: section.description,
          questions,
          subjectVisibility: section.subjectVisibility,
        };
      });
      return newSections;
    });
  }, [assessmentDeliveryData]);

  const allAnswers = useMemo(() => {
    if (!assessmentDeliveryData) {
      return [];
    }
    const answers = assertEdgesNonNull(assessmentDeliveryData.answers);
    return answers;
  }, [assessmentDeliveryData]);

  // Build answer map for quick lookup by ID
  const answerMap = useMemo(() => {
    const map = new Map();
    for (const answer of allAnswers) {
      map.set(answer.id, answer);
    }
    return map;
  }, [allAnswers]);

  // Build grouped answers map by question ID
  const groupedAnswersByQuestion = useMemo(() => {
    if (!assessmentDeliveryData?.groupedAnswers) {
      return new Map();
    }
    const map = new Map<number, NonNullable<GroupedAnswersNode>[]>();
    for (const groupedAnswer of assessmentDeliveryData.groupedAnswers) {
      if (!groupedAnswer) continue;
      const questionId = groupedAnswer.questionId;
      if (!map.has(questionId)) {
        map.set(questionId, []);
      }
      assertNonNull(map.get(questionId)).push(groupedAnswer);
    }
    return map;
  }, [assessmentDeliveryData]);

  return (
    <div>
      {sectionNodes.map((section) => {
        const sectionQuestions = section.questions;
        const hasVisibleQuestions = sectionQuestions.some((question) => {
          const answers = allAnswers.filter(
            (answer) => answer.questionId === question.question.id,
          );
          return answers.length > 0;
        });
        if (!hasVisibleQuestions) {
          return null;
        }
        const heading = section.title ? (
          <AssessmentLayout.SectionHeading>
            <AssessmentLayout.SectionHeadingTitle>
              {section.title}
            </AssessmentLayout.SectionHeadingTitle>
            {section.description && !isEmptyValue(section.description) && (
              <AssessmentLayout.SectionHeadingDescription>
                <TextareaWysiwyg
                  editable={false}
                  className="bg-white"
                  value={section.description}
                />
              </AssessmentLayout.SectionHeadingDescription>
            )}
          </AssessmentLayout.SectionHeading>
        ) : null;

        if (sectionQuestions.length === 0) {
          return null;
        }

        return (
          <AssessmentLayout.SectionContainer key={section.id}>
            {heading}
            {sectionQuestions.length !== 0 && (
              <AssessmentLayout.SectionRegularQuestions>
                {sectionQuestions.map((node) => {
                  const { question } = node;
                  const answersForQuestion = allAnswers.filter(
                    (answer) => answer.questionId === question.id,
                  );
                  const groupedAnswers = groupedAnswersByQuestion.get(
                    question.id,
                  );
                  return (
                    <EngagementSurveyDeliveryQuestion
                      key={question.id}
                      answers={answersForQuestion}
                      question={question}
                      isGroupingEnabled={!!viewOptions.groupBy}
                      groupedAnswers={groupedAnswers}
                      answerMap={answerMap}
                    />
                  );
                })}
              </AssessmentLayout.SectionRegularQuestions>
            )}
          </AssessmentLayout.SectionContainer>
        );
      })}
    </div>
  );
};

export default EngagementSurveyDeliveryQuestionSections;
